"use client";

import React from "react";
import { useRouter } from "next/navigation";
import { useTabs } from "./TabsContext";
import { cn } from "utils/utils";

interface RouterIntegration {
  href: string;
  paramName: string;
}

interface TabsTriggerProps {
  children: React.ReactNode;
  value: string | number;
  className?: string;
  icon?: React.ReactNode;
  badge?: React.ReactNode | number;
  routerIntegration?: RouterIntegration;
}

export function TabsTrigger({
  children,
  value,
  className,
  icon,
  badge,
  routerIntegration,
}: TabsTriggerProps) {
  const { activeTab, setActiveTab, variant } = useTabs();
  const router = useRouter();
  const isActive = activeTab === value;

  const handleClick = () => {
    setActiveTab(value);

    // Optional router integration (for Settings tabs)
    if (routerIntegration) {
      const url = new URL(routerIntegration.href, window.location.origin);
      url.searchParams.set(routerIntegration.paramName, String(value));
      router.push(url.toString());
    }
  };

  return (
    <a
      role="tab"
      className={cn(
        "tab",
        variant !== "lift" && "tab-border-3",
        variant === "lift" && "[--tab-height:2.5rem]",
        isActive && "tab-active text-primary",
        isActive &&
          variant === "lift" &&
          "bg-base-200 relative z-10 -mb-px [--tab-bg:var(--color-base-200)]",
        isActive && variant !== "lift" && "border-[rgb(168,139,255)!important]",
        className
      )}
      aria-selected={isActive}
      aria-controls={`tab-panel-${value}`}
      id={`tab-trigger-${value}`}
      onClick={handleClick}
    >
      <div className="flex items-center gap-2">
        {icon && <span className="flex items-center">{icon}</span>}
        <span>{children}</span>
        {badge !== undefined && badge !== null && (
          <span
            className={cn(
              "badge badge-sm",
              isActive ? "badge-primary" : "badge-ghost"
            )}
          >
            {badge}
          </span>
        )}
      </div>
    </a>
  );
}
